import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { type MouseEvent, useCallback } from 'react';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';

import { Supplier, type TestProps } from '../../models';
import { useAddSupplierMutation } from '../../slices/apis/testingCentreApi';
import Tests from './SupplierTestsConfig';

export default function NewSupplier() {
	const navigate = useNavigate();
	const [addSupplier, { isSuccess }] = useAddSupplierMutation();

	const formik = useFormik({
		initialValues: Supplier.schema.cast({}),
		validationSchema: Supplier.schema,
		onSubmit: async (values) => {
			await addSupplier({
				...values,
				testIds: Supplier.formatTestsPayload(values.tests as TestProps[]),
			});
		},
	});

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	useEffect(() => {
		if (isSuccess) {
			navigate('/dashboard/admin/suppliers');
		}
	}, [navigate, isSuccess]);

	return (
		<>
			<Helmet>
				<title> New Test Centre - {process.env.REACT_APP_ORG}</title>
			</Helmet>
			<Grid container justifyContent="start">
				<Grid size={{ md: 6 }}>
					<Typography variant="h4" gutterBottom>
						New Test Centre
					</Typography>
					<Card>
						<CardContent>
							<form onSubmit={formik.handleSubmit}>
								<Grid container spacing={2}>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="name"
											name="name"
											label="Name"
											variant="standard"
											value={formik.values.name}
											onChange={formik.handleChange}
											error={formik.touched.name && Boolean(formik.errors.name)}
											helperText={formik.touched.name && formik.errors.name}
										/>
									</Grid>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="phone"
											name="phone"
											label="Phone"
											variant="standard"
											inputProps={{ inputMode: 'numeric', maxLength: 10 }}
											value={formik.values.phone}
											onChange={formik.handleChange}
											error={formik.touched.phone && Boolean(formik.errors.phone)}
											helperText={formik.touched.phone && formik.errors.phone}
										/>
									</Grid>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="email"
											name="email"
											label="Email"
											variant="standard"
											type="email"
											value={formik.values.email}
											onChange={formik.handleChange}
											error={formik.touched.email && Boolean(formik.errors.email)}
											helperText={formik.touched.email && formik.errors.email}
										/>
									</Grid>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="address"
											name="address"
											label="Address"
											variant="standard"
											value={formik.values.address}
											onChange={formik.handleChange}
											error={formik.touched.address && Boolean(formik.errors.address)}
											helperText={formik.touched.address && formik.errors.address}
										/>
									</Grid>
									<Grid size={{ md: 12 }}>
										{/* @ts-ignore */}
										<Tests formik={formik} />
									</Grid>
									<Grid size={{ md: 4 }}>
										<Grid container justifyContent="start" spacing={4}>
											<Grid size={{ md: 6 }}>
												<Button loading={formik.isSubmitting} variant="outlined" color="primary" type="submit">
													<span>Save</span>
												</Button>
											</Grid>
											<Grid size={{ md: 6 }}>
												<Button
													color="error"
													variant="outlined"
													data-route="/dashboard/admin/suppliers"
													onClick={handleNavigate}
												>
													Cancel
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</form>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</>
	);
}
